import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import * as dayjs from 'dayjs';

import { TimeRangeContext } from '../../context/TimeRangeContext';
import { buildRedirectQuery } from '../../helpers/timeRangeUtils';
import { PERIOD_CUSTOM } from '../../component/Constants';
import { useInstanceFilters } from '../../hooks/useInstanceFilters';
import { useHostFilters } from '../../hooks/useHostFilters';
import SensorStatusApi from '../../api/SensorStatusApi';

import InstanceTarget from '../../types/instance/InstanceTarget';
import HostTarget from '../../types/host/HostTarget';

import FilterPanel from '../../component/FilterPanel';
import SearchInput from '../../component/SearchInput';
import StatusIcon from '../../component/StatusIcon';
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import HeaderActions from '../../container/header_actions';
import Breadcrumb from '../../container/breadcrumb';
import PageContent from '../../container/page_content';
import ConditionalRender from '../../helpers/ConditionalRender';
import {
    computeCellLayout,
    bucketBounds,
    rangeEndTimestamp,
    statusKind,
    summarizeBucket,
    estimatedBreakdown,
    StatusKind,
    STATUS_LABEL,
} from './timeline';

import './style.scss';

interface Props {
    instances: InstanceTarget[];
    hosts: HostTarget[];
    toggleMenu: Function;
}

type ActiveTab = 'instances' | 'hosts';

// Hosts tab is hidden until the backend endpoints are available.
const HOSTS_TAB_ENABLED = false;

// DOM-safety valve only: every timeslice gets its own cell so the display
// granularity matches the selected interval; slices are merged only when a
// (URL-crafted) range/interval combo exceeds this many cells. All interval
// choices offered by the period selector stay well below it.
const MAX_CELLS = 720;

// Available timeline display styles, remembered between visits.
type DisplayMode = 'bars' | 'segments' | 'squares';
const DISPLAY_MODES: { key: DisplayMode; label: string; icon: string }[] = [
    { key: 'bars', label: 'Bars', icon: 'fa-light fa-chart-simple' },
    { key: 'segments', label: 'Timeline', icon: 'fa-light fa-bars-progress' },
    { key: 'squares', label: 'Squares', icon: 'fa-light fa-grid' },
];
const DISPLAY_MODE_STORAGE_KEY = 'sensor-status-display-mode';

// Roughly how many time labels to show across the axis. Labels are spaced an
// even number of square-columns apart so they line up with the columns.
const LABEL_TARGET = 12;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Legend order — healthy first, then the states that need attention.
const LEGEND_KINDS: StatusKind[] = ['ok', 'error', 'mixed', 'stopped', 'nodata'];

interface StatusRun {
    kind: StatusKind;
    start: number;
    end: number; // exclusive
}

// Collapse a status array into contiguous same-kind runs (used by the segments
// display). Grouping by kind rather than raw value merges adjacent RMS-blend
// slices into one continuous "Mixed" segment.
const computeRuns = (statuses: number[]): StatusRun[] => {
    const runs: StatusRun[] = [];
    let i = 0;
    while (i < statuses.length) {
        const kind = statusKind(statuses[i]);
        let j = i + 1;
        while (j < statuses.length && statusKind(statuses[j]) === kind) j++;
        runs.push({ kind, start: i, end: j });
        i = j;
    }
    return runs;
};

const SensorStatus: React.FC<Props> = (props) => {
    const { timeRange, applyPeriod } = useContext(TimeRangeContext);
    const [activeTab, setActiveTab] = useState<ActiveTab>('instances');
    const [timeslices, setTimeslices] = useState<string[]>([]);
    const [historyMap, setHistoryMap] = useState<Map<number, number[]>>(new Map());
    const [orderedIds, setOrderedIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    // Legend-driven quick filter: empty set = show all rows; otherwise only
    // rows whose current (latest) status kind is selected.
    const [kindFilter, setKindFilter] = useState<Set<StatusKind>>(new Set());
    const [displayMode, setDisplayMode] = useState<DisplayMode>(() => {
        const saved = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY) as DisplayMode | null;
        return saved && DISPLAY_MODES.some(m => m.key === saved) ? saved : 'bars';
    });

    const changeDisplayMode = (mode: DisplayMode) => {
        setDisplayMode(mode);
        localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, mode);
    };

    // Monotonic id so that an in-flight fetch from a previous tab/time range
    // cannot overwrite state after the user switches.
    const requestIdRef = useRef(0);

    const instanceFilters = useInstanceFilters({ instances: props.instances, shouldFetch: false });
    // Skip the hosts hook entirely while the Hosts tab is hidden so it doesn't
    // fetch agents on mount.
    const hostFilters = useHostFilters({ hosts: HOSTS_TAB_ENABLED ? props.hosts : [] });

    const fetchData = useCallback(async () => {
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const kind = activeTab === 'instances' ? 'datasource' : 'host';
            const [slices, history] = await Promise.all([
                SensorStatusApi.getTimeslices(kind, timeRange),
                SensorStatusApi.getHistory(kind, timeRange),
            ]);
            if (reqId !== requestIdRef.current) return;
            setTimeslices(slices);
            setHistoryMap(new Map(history.map(e => [e.id, e.sensorstatushistory])));
            setOrderedIds(history.map(e => e.id));
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    }, [timeRange, activeTab]);

    useEffect(() => { void fetchData(); }, [fetchData]);

    // Derive actual interval in seconds from adjacent timeslice timestamps.
    // This works for any interval (15s, 1min, 5min, etc.) without relying on
    // timeRange.interval which is 0 in auto-mode.
    const actualIntervalSeconds = useMemo(() => {
        if (timeslices.length < 2) return 60;
        const a = (dayjs as any)(timeslices[0]).valueOf();
        const b = (dayjs as any)(timeslices[1]).valueOf();
        return Math.max(1, Math.round((b - a) / 1000));
    }, [timeslices]);

    const rangeSeconds = timeslices.length * actualIntervalSeconds;

    // Number of cells per row (same for every row): one cell per timeslice,
    // so what's drawn is exactly the selected interval granularity.
    const { cellCount, slicesPerBucket } = useMemo(
        () => computeCellLayout(timeslices.length, MAX_CELLS),
        [timeslices.length],
    );

    // The timeslice timestamp at the start of each square's bucket. The axis
    // labels and the squares are both laid out as `cellCount` equal cells, so
    // labelling by bucket keeps the labels aligned above their columns.
    const bucketStarts = useMemo(
        () => Array.from({ length: cellCount }, (_, b) => timeslices[b * slicesPerBucket]),
        [timeslices, cellCount, slicesPerBucket],
    );

    // Seconds covered by one square.
    const bucketSeconds = slicesPerBucket * actualIntervalSeconds;

    // End of the whole range — one interval past the last slice's start. Used
    // as the tooltip end label for the final bucket/segment.
    const rangeEnd = useMemo(
        () => rangeEndTimestamp(timeslices, actualIntervalSeconds),
        [timeslices, actualIntervalSeconds],
    );

    // Clicking a cell/segment zooms the global time range into that bucket, so
    // e.g. a Mixed cell can be drilled into to see what happened inside it.
    const zoomToSlices = useCallback((startIdx: number, endIdx: number) => {
        const startTs = timeslices[startIdx];
        const endTs = timeslices[endIdx] ?? rangeEnd;
        if (!startTs || !endTs) return;
        const from = (dayjs as any).tz(startTs, timeRange.timezone);
        const to = (dayjs as any).tz(endTs, timeRange.timezone);
        applyPeriod(PERIOD_CUSTOM, from, to, timeRange.timezone);
    }, [timeslices, rangeEnd, timeRange.timezone, applyPeriod]);

    // Label every Nth column so the labels are evenly spaced and aligned with
    // the square columns.
    const labelStride = useMemo(
        () => Math.max(1, Math.round(cellCount / LABEL_TARGET)),
        [cellCount],
    );

    const labelBuckets = useMemo(() => {
        const out: number[] = [];
        for (let b = 0; b < cellCount; b += labelStride) out.push(b);
        return out;
    }, [cellCount, labelStride]);

    const labelStepSeconds = labelStride * bucketSeconds;

    // Per-label text. For single-day ranges: "HH:mm". For multi-day ranges:
    // show "d Mon" the first time a new calendar date appears, then "HH:mm"
    // for subsequent labels on the same day.
    const labelText = useMemo(() => {
        const map = new Map<number, string>();
        labelBuckets.forEach((b, pos) => {
            const ts = bucketStarts[b];
            if (!ts) return;
            if (rangeSeconds <= 86400) { map.set(b, ts.slice(11, 16)); return; }
            const day = parseInt(ts.slice(8, 10), 10);
            const mon = MONTH_SHORT[parseInt(ts.slice(5, 7), 10) - 1];
            if (labelStepSeconds >= 86400) { map.set(b, `${day} ${mon}`); return; }
            const prevTs = pos > 0 ? bucketStarts[labelBuckets[pos - 1]] : null;
            map.set(b, (!prevTs || prevTs.slice(0, 10) !== ts.slice(0, 10)) ? `${day} ${mon}` : ts.slice(11, 16));
        });
        return map;
    }, [labelBuckets, bucketStarts, rangeSeconds, labelStepSeconds]);

    // Format a timeslice timestamp for a square tooltip: "HH:mm" for single-day
    // ranges, "d Mon HH:mm" once the range spans more than a day.
    const fmtTooltipTime = useCallback((ts?: string): string => {
        if (!ts) return '';
        const hm = ts.slice(11, 16);
        if (rangeSeconds <= 86400) return hm;
        const day = parseInt(ts.slice(8, 10), 10);
        const mon = MONTH_SHORT[parseInt(ts.slice(5, 7), 10) - 1];
        return `${day} ${mon} ${hm}`;
    }, [rangeSeconds]);

    const filteredInstances = useMemo(() => {
        const base = instanceFilters.getFilteredInstances(props.instances);
        if (!searchFilter) return base;
        const lower = searchFilter.toLowerCase();
        return base.filter(x => x.name.toLowerCase().includes(lower));
    }, [instanceFilters, props.instances, searchFilter]);

    const filteredHosts = useMemo(() => {
        const base = hostFilters.getFilteredHosts(props.hosts);
        if (!searchFilter) return base;
        const lower = searchFilter.toLowerCase();
        return base.filter(x => x.name.toLowerCase().includes(lower));
    }, [hostFilters, props.hosts, searchFilter]);

    const rows = useMemo(() => {
        const items = activeTab === 'instances' ? filteredInstances : filteredHosts;
        const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
        return [...items]
            .sort((a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity))
            .map(item => {
                const statuses = historyMap.get(item.id) ?? [];
                // The legend is a colour key: selecting a colour shows every
                // row that CONTAINS at least one cell of that colour anywhere
                // in the visible range (union across selected colours).
                const kinds = new Set<StatusKind>(statuses.length ? statuses.map(statusKind) : ['nodata']);
                return { id: item.id, name: item.name, message: item.message, kinds };
            })
            .filter(row => kindFilter.size === 0 || Array.from(kindFilter).some(k => row.kinds.has(k)));
    }, [activeTab, filteredInstances, filteredHosts, orderedIds, historyMap, kindFilter]);

    const toggleKindFilter = (kind: StatusKind) => {
        setKindFilter(prev => {
            const next = new Set(prev);
            if (next.has(kind)) {
                next.delete(kind);
            } else {
                next.add(kind);
            }
            return next;
        });
    };

    const switchTab = (tab: ActiveTab) => {
        // Bump the request id so any in-flight response from the previous tab
        // is discarded when it resolves.
        requestIdRef.current++;
        setActiveTab(tab);
        setSearchFilter('');
        setKindFilter(new Set());
        setTimeslices([]);
        setHistoryMap(new Map());
        setOrderedIds([]);
    };

    const filterGroups = activeTab === 'instances'
        ? [
            { name: 'sensorstatus', label: 'Sensor Status', options: instanceFilters.availableFilterOptions.sensorstatuses, selectedValues: instanceFilters.filters.sensorstatus, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('sensorstatus', v) },
            { name: 'type', label: 'Type', options: instanceFilters.availableFilterOptions.types, selectedValues: instanceFilters.filters.type, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('type', v) },
            { name: 'tag', label: 'Tag', options: instanceFilters.availableFilterOptions.tags, selectedValues: instanceFilters.filters.tag, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('tag', v) },
            { name: 'host', label: 'Host', options: instanceFilters.availableFilterOptions.hosts, selectedValues: instanceFilters.filters.host, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('host', v) },
            { name: 'hostType', label: 'Host Type', options: instanceFilters.availableHostTypes, selectedValues: instanceFilters.filters.hostType, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('hostType', v) },
            { name: 'agent', label: 'Agent', options: instanceFilters.availableFilterOptions.agents, selectedValues: instanceFilters.filters.agent, onSelectionChange: (v: string[]) => instanceFilters.updateFilter('agent', v) },
        ]
        : [
            { name: 'sensorstatus', label: 'Sensor Status', options: hostFilters.availableFilterOptions.sensorstatuses, selectedValues: hostFilters.filters.sensorstatus, onSelectionChange: (v: string[]) => hostFilters.updateFilter('sensorstatus', v) },
            { name: 'type', label: 'Type', options: hostFilters.availableFilterOptions.types, selectedValues: hostFilters.filters.type, onSelectionChange: (v: string[]) => hostFilters.updateFilter('type', v) },
            { name: 'agent', label: 'Agent', options: hostFilters.availableFilterOptions.agents, selectedValues: hostFilters.filters.agent, onSelectionChange: (v: string[]) => hostFilters.updateFilter('agent', v) },
        ];

    const clearAllFilters = activeTab === 'instances' ? instanceFilters.clearAllFilters : hostFilters.clearAllFilters;

    return (
        <PageContent title='Sensor Status - DBmarlin' description='Sensor status over time for all monitored DB instances and hosts'>

            <div className="sensor-status">

                {/* Heading */}
                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb heading="Sensor Status">
                        <Link to="/sensor-status">Analysis</Link>
                        <NavLink to="/sensor-status" className="current-breadcrumb-link">Sensor Status</NavLink>
                        <PeriodBreadcrumb />
                    </Breadcrumb>
                    <HeaderActions toggleMenu={props.toggleMenu} refresh={fetchData} />
                </div>

                <div className="loader">
                    <ConditionalRender if={loading}>
                        <div className="bar" />
                    </ConditionalRender>
                </div>

                {/* Tabs — Hosts tab hidden until backend APIs are implemented */}
                <div className="menu-top nav nav-tabs" role="tablist">
                    <a
                        role="tab"
                        className={activeTab === 'instances' ? 'active' : ''}
                        onClick={() => switchTab('instances')}
                        style={{ cursor: 'pointer' }}
                    >
                        <i className="fal fa-database fa-fw" aria-hidden="true" />
                        DB Instances
                    </a>
                    <ConditionalRender if={HOSTS_TAB_ENABLED}>
                        <a
                            role="tab"
                            className={activeTab === 'hosts' ? 'active' : ''}
                            onClick={() => switchTab('hosts')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="fal fa-server fa-fw" aria-hidden="true" />
                            Hosts
                        </a>
                    </ConditionalRender>
                </div>

                {/* Filter Panel */}
                <div className="row mt-2">
                    <div className="col">
                        <FilterPanel
                            filterGroups={filterGroups}
                            onClearAll={clearAllFilters}
                            isLoading={loading}
                        />
                    </div>
                </div>

                {/* Search + Grid */}
                <div className="row mt-2">
                    <div className="col">
                        <div className="card">
                            <div className="card-body p-2">

                                <div className="sensor-status__toolbar mb-2">
                                    <div className="sensor-status__search">
                                        <SearchInput
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                            onClear={() => setSearchFilter('')}
                                            placeholder={`Search ${activeTab}...`}
                                        />
                                    </div>
                                    <div className="sensor-status__display-toggle" role="group" aria-label="Timeline display style">
                                        {DISPLAY_MODES.map(m => (
                                            <button
                                                key={m.key}
                                                type="button"
                                                className={displayMode === m.key ? 'active' : ''}
                                                onClick={() => changeDisplayMode(m.key)}
                                                data-tooltip-id="global-tooltip"
                                                data-tooltip-content={`${m.label} view`}
                                            >
                                                <i className={`${m.icon} fa-fw`} aria-hidden="true" />
                                                <span>{m.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Time-axis header — axis on the left, name/status column on the right */}
                                <div className="sensor-status__header">
                                    <div className="sensor-status__axis">
                                        {Array.from({ length: cellCount }, (_, b) => {
                                            const text = labelText.get(b);
                                            return (
                                                <div key={b} className="sensor-status__axis-cell">
                                                    {text && <span className="sensor-status__axis-label">{text}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="sensor-status__name-heading">
                                        {activeTab === 'instances' ? 'DB Instance' : 'Hostname'}
                                    </div>
                                </div>

                                {/* Status rows */}
                                <ConditionalRender if={rows.length === 0 && !loading}>
                                    <div className="text-muted text-center py-3">No data available for the selected time range and filters.</div>
                                </ConditionalRender>
                                {rows.map(row => {
                                    const statuses = historyMap.get(row.id) ?? [];
                                    const total = timeslices.length;
                                    return (
                                        <div key={row.id} className="sensor-status__row">
                                            <div className={`sensor-status__bar sensor-status__bar--${displayMode}`}>
                                                {displayMode === 'segments'
                                                    ? /* Continuous runs — width shows how long each state lasted */
                                                    (statuses.length ? computeRuns(statuses) : [{ kind: 'nodata' as StatusKind, start: 0, end: total || 1 }]).map((run, ri, arr) => {
                                                        const span = total || 1;
                                                        const left = (run.start / span) * 100;
                                                        const width = ((run.end - run.start) / span) * 100;
                                                        const startLabel = fmtTooltipTime(timeslices[run.start]);
                                                        // Range ends where the next run begins; the final
                                                        // run ends at the end of the whole range.
                                                        const endLabel = fmtTooltipTime(timeslices[run.end] ?? rangeEnd);
                                                        const range = startLabel ? ` (${startLabel}–${endLabel})` : '';
                                                        const breakdown = run.kind === 'mixed' && statuses.length
                                                            ? `: ${estimatedBreakdown(statuses, run.start, run.end, actualIntervalSeconds)}`
                                                            : '';
                                                        const radius = arr.length === 1 ? '2px'
                                                            : ri === 0 ? '2px 0 0 2px'
                                                            : ri === arr.length - 1 ? '0 2px 2px 0'
                                                            : '0';
                                                        return (
                                                            <div
                                                                key={ri}
                                                                className={`sensor-status__segment sensor-status__c-${run.kind}`}
                                                                style={{ left: `${left}%`, width: `${width}%`, borderRadius: radius }}
                                                                data-tooltip-id="global-tooltip"
                                                                data-tooltip-content={`${STATUS_LABEL[run.kind]}${range}${breakdown}`}
                                                                onClick={() => zoomToSlices(run.start, run.end)}
                                                            />
                                                        );
                                                    })
                                                    : /* Bars / squares — one mark per downsampled time bucket */
                                                    Array.from({ length: cellCount }, (_, b) => {
                                                        // Each tooltip range ends where the next bucket
                                                        // begins; the final bucket ends at the range end.
                                                        const { start: startIdx, end: endIdx } = bucketBounds(b, slicesPerBucket, total);
                                                        const kind = statuses.length
                                                            ? summarizeBucket(statuses, startIdx, endIdx).kind
                                                            : 'nodata' as StatusKind;
                                                        const startLabel = fmtTooltipTime(timeslices[startIdx]);
                                                        const endLabel = fmtTooltipTime(timeslices[endIdx] ?? rangeEnd);
                                                        const range = startLabel ? ` (${startLabel}–${endLabel})` : '';
                                                        const breakdown = kind === 'mixed'
                                                            ? `: ${estimatedBreakdown(statuses, startIdx, endIdx, actualIntervalSeconds)}`
                                                            : '';
                                                        return (
                                                            <div key={b} className="sensor-status__cell">
                                                                <div
                                                                    className={`sensor-status__mark sensor-status__c-${kind}`}
                                                                    data-tooltip-id="global-tooltip"
                                                                    data-tooltip-content={`${STATUS_LABEL[kind]}${range}${breakdown}`}
                                                                    onClick={() => zoomToSlices(startIdx, endIdx)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                            </div>

                                            {/* Current status badge + name (linked to the target's detail page), on the right */}
                                            <div className="sensor-status__name">
                                                {activeTab === 'instances'
                                                    ? <StatusIcon instanceId={row.id} />
                                                    : <StatusIcon hostId={row.id} />}
                                                <Link
                                                    to={activeTab === 'instances'
                                                        ? `/instances/${row.id}/activity?${buildRedirectQuery(timeRange)}`
                                                        : `/hosts/${row.id}?${buildRedirectQuery(timeRange)}`}
                                                    className="sensor-status__name-text"
                                                >{row.name}</Link>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Legend — stays visible while a legend filter is active even if it
                                    matches no rows, so the filter can always be cleared */}
                                <ConditionalRender if={rows.length > 0 || kindFilter.size > 0}>
                                    <div className="sensor-status__legend">
                                        {LEGEND_KINDS.map(kind => {
                                            const selected = kindFilter.has(kind);
                                            const dimmed = kindFilter.size > 0 && !selected;
                                            return (
                                                <button
                                                    key={kind}
                                                    type="button"
                                                    className={`sensor-status__legend-item sensor-status__legend-item--${kind}${selected ? ' active' : ''}${dimmed ? ' inactive' : ''}`}
                                                    onClick={() => toggleKindFilter(kind)}
                                                    aria-pressed={selected}
                                                    data-tooltip-id="global-tooltip"
                                                    data-tooltip-content={selected
                                                        ? `Stop filtering by ${STATUS_LABEL[kind]}`
                                                        : `Show ${activeTab} with ${STATUS_LABEL[kind]} periods`}
                                                >
                                                    <i className="fas fa-square" />
                                                    {STATUS_LABEL[kind]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </ConditionalRender>

                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </PageContent>
    );
};

export default SensorStatus;
